import { ReactNode } from 'react';
import RefundTerms from '@v2/brands/magzoom/components/RefundTerms';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import ContentCard from '@v2/shared/components/ContentCard';
import PageHeading from '@v2/shared/components/PageHeading';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';

/**
 * A question, on its own card.
 *
 * The `id` is on the heading rather than on the card so that following a link to it puts
 * the question at the top of the viewport rather than the card's padding.
 *
 * These ids are load-bearing and are the legacy page's, unchanged — `#renewal-service` is
 * where MagZoom's account rail sends a customer to read about auto-renewal
 * (`auto_renewals_about` in config/services/sites.php) and where two of the dated terms
 * documents link. The legacy page carried them as `<a name="...">`, which has been
 * obsolete since HTML5; the fragments they answer to are unchanged.
 */
function Question({ id, question, children }: { id: string; question: string; children: ReactNode }) {
  return (
    <ContentCard>
      <h2 id={id}>{question}</h2>
      {children}
    </ContentCard>
  );
}

/**
 * /faq.
 *
 * A card per question, which is the institutional pages' section pattern — TopMags' FAQ
 * and DiscountMags' accessibility and copyright pages are the same shape. The words are
 * the legacy page's, unchanged.
 *
 * The legacy page opened with a list of links into its answers and it is kept, because it
 * is the page's own navigation. Unlike MagSpot's, MagZoom's questions are not grouped, so
 * they are `<h2>`s directly under the H1 — they were bold anchors inside a card header
 * before, and headings under nothing.
 *
 * That list had a ninth entry, "What's your cancellation Policy?", pointing at `#cancel`.
 * No such anchor has ever existed on the page, so the link did nothing. It is dropped
 * rather than reproduced: "What is your refund policy?" above it is the answer, and
 * shipping a jump link that goes nowhere into a new page is not fidelity.
 */
export default function Faq() {
  const { buildUrl } = useUrlGenerator();

  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Frequently Asked Questions' }]} />

      <PageHeading eyebrow="Customer Information">Frequently Asked Questions</PageHeading>

      <div className="prose mt-6 flex flex-col gap-6">
        <ContentCard>
          <ul>
            <li>
              <a href="#delivery-time">What&apos;s the estimated delivery wait time?</a>
            </li>
            <li>
              <a href="#delivery">Where do you deliver to?</a>
            </li>
            <li>
              <a href="#address">How do I update an address?</a>
            </li>
            <li>
              <a href="#refund-policy">What is your refund policy?</a>
            </li>
            <li>
              <a href="#renew">How do I review a subscription?</a>
            </li>
            <li>
              <a href="#duplicate">Why am I receiving duplicate magazines?</a>
            </li>
            <li>
              <a href="#price-match">Does MagZoom.com price match?</a>
            </li>
            <li>
              <a href="#renewal-service">What is the Renewal Service?</a>
            </li>
          </ul>
        </ContentCard>

        <Question id="delivery-time" question="What's the estimated delivery wait time?">
          <p>
            Each magazine is shipped to you directly by the publisher. Many magazines are pre-printed
            in advance, therefore, wait time to receive your first issue can vary up to 12 weeks.
          </p>
        </Question>

        <Question id="delivery" question="Where do you deliver to?">
          <p>
            We delivery all over the United States including APO address and correctional facilities.
            Please keep in mind when ordering for recipients to check with the facility to ensure they
            allow the title(s) and to receive the shipping label requirements. ID numbers are required
            by most facilities right after the Last Name. If labels are printed incorrect, facilities
            will not deliver.
          </p>
        </Question>

        <Question id="address" question="How do I update an address?">
          <p>
            Please{' '}
            <a href={buildUrl('/contact')}>contact a customer service representative</a> at any time
            to update or change an address. It may take up to 6-8 weeks for your address change to
            take effect since publishers pre-print labels. By requesting mail forwarding with your
            post office, you should not miss any issues.
          </p>
        </Question>

        {/* The same terms `/refund` publishes, and the same component, so the
            two answers cannot drift apart. */}
        <Question id="refund-policy" question="What is your refund policy?">
          <RefundTerms />
        </Question>

        <Question id="renew" question="How do I review a subscription?">
          <p>
            When placing an order you will have the option through a dropdown in your cart to select
            &ldquo;Renewal&rdquo; if you would like your subscription to renew with the publisher for
            additional terms.
          </p>
        </Question>

        <Question id="duplicate" question="Why am I receiving duplicate magazines?">
          <p>
            If you are receiving the same magazine more than once you could have multiple orders
            through different sites or a previous subscription. Please{' '}
            <a href={buildUrl('/contact')}>contact us</a> to resolve this issue promptly.
          </p>
        </Question>

        <Question id="price-match" question="Does MagZoom.com price match?">
          <p>
            MagZoom.com ensures great prices for all of our subscriptions but if you find a
            competitor&apos;s price that beats ours we will happily price match within 30 days of your
            order date. Please <a href={buildUrl('/contact')}>contact a representative</a> with the
            URL to verify the offer.
          </p>
        </Question>

        <Question id="renewal-service" question="What is the Renewal Service?">
          <p>
            Our Renewal Service is an automatic renewal program that ensures you never miss an issue.
            Your subscription will automatically renew with a minimum discount of 10% off the list
            price at the time of each renewal. If for any reason we cannot offer this discount, we
            will notify you and will not automatically renew your subscription.
          </p>
          <p>
            If your magazine is discontinued or otherwise becomes unavailable, we&rsquo;ll
            automatically provide you with a subscription to a similar magazine of equal or greater
            value, at the same or better price. We&rsquo;ll send you advance notice of the
            substitution, and if you don&rsquo;t want the substitute magazine, you can cancel before
            the renewal is processed.
          </p>
          <p>
            We&rsquo;ll send you an initial reminder 30 days prior to your subscription renewal. Then,
            7 days before the renewal, we&rsquo;ll provide a follow-up email detailing the exact
            amount you will be charged.
          </p>
          {/*
           * The legacy answer's second link was `{customer.discountlocklink}`, a fixed
           * `customer.php?xCmd=dlshow` rather than anything per-customer. #1813 moved this
           * brand's DiscountLock pages onto the V2 account route, and `auto_renewals_list`
           * in config/services/sites.php names this one for MagZoom. See MagSpot's FAQ.
           */}
          <p>
            The Renewal Service comes with a satisfaction guarantee, so if at any time during your
            subscription you are unhappy with the service, you may cancel it at any time by{' '}
            <a href={buildUrl('/contact')}>contacting us</a> or{' '}
            <a href={buildUrl('/my-account/renewal-service')}>logging into your account</a>.
          </p>
        </Question>
      </div>
    </>
  );
}

Faq.layoutName = 'Layouts.Institutional';
